import { FC, useState } from 'react'
import { PureQueryOptions } from '@apollo/client'
import gql from 'graphql-tag'
import styled from 'styled-components'
import {
  CoverLetterScrap_CoverLetterFragment,
  CoverLetterScrap_CurrentUserFragment,
  useCoverLetterScrapUpdateMutation,
} from 'generated/graphql'
import LoginAlert from 'components/LoginAlert'
import useErrorSnackbar from 'hooks/useErrorSnackbar'

interface ChildrenProps {
  scrapCoverLetter: () => void
  loading: boolean
  scrapCount?: number
}

interface CoverLetterScrapProps {
  coverLetter?: CoverLetterScrap_CoverLetterFragment | null
  currentUser?: CoverLetterScrap_CurrentUserFragment | null
  refetchQueries?: PureQueryOptions[]
  children(state: ChildrenProps): void
}

const CoverLetterScrap: FC<CoverLetterScrapProps> = (props) => {
  const { coverLetter, currentUser, children, refetchQueries } = props

  const { enqueueApolloError } = useErrorSnackbar()
  const [showLoginAlert, setShowLoginAlert] = useState(false)
  const [coverLetterScrapUpdate, { loading }] =
    useCoverLetterScrapUpdateMutation({
      onError: enqueueApolloError,
      ...(refetchQueries && {
        refetchQueries,
        awaitRefetchQueries: true,
      }),
    })
  const scrapCount = coverLetter?.scrapCount || 0

  function closeLoginAlert() {
    setShowLoginAlert(false)
  }

  function scrapCoverLetter() {
    if (!coverLetter?.id) {
      return
    }

    if (!currentUser) {
      setShowLoginAlert(true)
      return
    }

    coverLetterScrapUpdate({
      optimisticResponse: {
        __typename: 'Mutation',
        coverLetterScrapUpdate: {
          __typename: 'CoverLetterScrapUpdatePayload',
          userScrap: {
            id: 'optimisticResponse_ID',
            __typename: 'UserScrap',
            user: {
              __typename: 'User',
              id: currentUser.id,
              scrapCount:
                (currentUser?.scrapCount || 0) +
                (coverLetter?.isScrapped ? -1 : 1),
            },
          },
          coverLetter: {
            id: coverLetter?.id,
            isScrapped: !coverLetter?.isScrapped,
            scrapCount:
              (coverLetter.scrapCount || 0) +
              (coverLetter?.isScrapped ? -1 : 1),
            __typename: 'CoverLetter',
          },
          scrapped: !coverLetter?.isScrapped,
        },
      },
      variables: {
        coverLetterScrapUpdateInput: {
          coverLetterID: coverLetter?.id,
          referer: `/coverLetter/${coverLetter?.id}`,
        },
      },
    })
  }

  return (
    <>
      {children({ loading, scrapCoverLetter, scrapCount })}
      <LoginAlert
        title={
          <>
            로그인 후 <StyledPrimaryText>스크랩이</StyledPrimaryText>{' '}
            가능합니다.
          </>
        }
        DialogProps={{ open: showLoginAlert }}
        onRequestClose={closeLoginAlert}
      />
    </>
  )
}

export default CoverLetterScrap

const StyledPrimaryText = styled.span`
  font-size: inherit;
  font-weight: inherit;
  color: ${({ theme }) => theme.color.primary_2};
`

gql`
  fragment CoverLetterScrap_currentUser on User {
    id
    scrapCount
    isCheckedParagraphScrapGuide
  }
  fragment CoverLetterScrap_coverLetter on CoverLetter {
    id
    isScrapped
    scrapCount
  }
`
